import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedDate, convertToNumberKWD } from "../../utils/common";
import {
  calculateVariantItemPrice,
  calculateChargeableItems,
  calculateItemDisplayPrice,
  calculateTotalChargeableItemsCost,
  calculateTotalPackageBasePrice,
  calculatePackagePrice,
  calculateItemDisplayPriceInPackage,
  getSelectedOptions
} from "../../utils/catering";
import { validatePackageCompletion } from "../../utils/paxCalculations";
import ShareQuotationIcon from "../../assets/icons/ShareQuotationIcon";
import ShareQuotationModal from "./ShareQuotationModal";
import { useSelector, useDispatch } from "react-redux";

import { selectCateringCartPackages, selectCateringCartItems, selectCateringCartSubtotal, selectCateringCartTotal, selectCateringCartFilters, selectCateringRestaurantId, selectCateringBrandsList, selectIsPastOrderRestoration, selectPendingPayment, selectCateringCartNotes } from "../../redux/features/restaurants/restaurantSelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { selectCateringSearchedCustomer, selectCateringSelectedAddress } from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectSearchedCompany, selectCompanySelectedAddress } from "../../redux/features/company/companySelectors";
import { addToCateringCart } from "../../api/post/addToCateringCart";
import { processManualOrder } from "../../api/post/processManualOrder";
import { processCateringManualOrder } from "../../api/post/processCateringManualOrder";
import { getCateringOrderStatus } from "../../api/get/getCateringOrderStatus";
import { resendCateringPaymentLink } from "../../api/post/resendCateringPaymentLink";
import { transformCateringCartPayload } from "../../utils/catering";
import { emptyCateringCartAsync } from "../../redux/features/restaurants/restaurantThunks";
import { clearCateringCart, setCateringCartFilters, setIsPastOrderRestoration, setPendingPayment, clearPendingPayment, setCateringCartNotes } from "../../redux/features/restaurants/restaurantSlice";

const CateringOrderSummaryReadonly = ({ selectedPackages, showPaymentOptions = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formattedDate = getFormattedDate();
  const filters = useSelector(selectCateringCartFilters);

  const [orderedPackages, setOrderedPackages] = useState([]);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null); // Store order_no and reference_number for polling
  const [isResendingPayment, setIsResendingPayment] = useState(false);
  const [resendPaymentError, setResendPaymentError] = useState(null);
  const pollingIntervalRef = useRef(null);
  
  // Get pending payment state from Redux
  const pendingPaymentFromRedux = useSelector(selectPendingPayment);
  const brandList = useSelector(selectCateringBrandsList);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cateringRestaurantName = brandList?.find(brand => brand.id == cateringRestaurantId)?.name_en;
  const serviceType = useSelector(selectServiceType);
  
  // Get notes from Redux (retained from CateringOrderSummary)
  const notes = useSelector(selectCateringCartNotes);
  
  // Check if this is a past order restoration (should hide Book Slot button) - from Redux
  const isPastOrderRestoration = useSelector(selectIsPastOrderRestoration);
  
  // Individual catering selectors
  const cateringCustomerInfo = useSelector(selectCateringSearchedCustomer);
  const cateringSelectedAddress = useSelector(selectCateringSelectedAddress);
  
  // Corporate catering selectors
  const companyInfo = useSelector(selectSearchedCompany);
  const companySelectedAddress = useSelector(selectCompanySelectedAddress);

  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringCartItems = useSelector(selectCateringCartItems);
  const cateringCartSubtotal = useSelector(selectCateringCartSubtotal);
  const cateringCartTotal = useSelector(selectCateringCartTotal);

  // Don't clean up the flag on component unmount since we navigate between pages
  // Only clean it up after successful order processing or explicit user action
  
  // Restore pending payment state when component mounts
  useEffect(() => {
    if (pendingPaymentFromRedux) {
      console.log('Restoring pending payment state from Redux:', pendingPaymentFromRedux);
      setShowPendingPayment(true);
      setPaymentStatus(pendingPaymentFromRedux.status);
      setOrderInfo(pendingPaymentFromRedux.orderInfo);
      
      // If payment is still pending, resume polling
      if (pendingPaymentFromRedux.status === "Pending" && pendingPaymentFromRedux.orderInfo?.order_no) {
        startPaymentStatusPolling(
          pendingPaymentFromRedux.orderInfo.order_no,
          pendingPaymentFromRedux.orderInfo.reference_number
        );
      }
    }
  }, []);

  // Validate if all required package items have been added based on pax_limit
  const packageValidation = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0) {
      return {
        isValid: false,
        missingCategories: [],
        message: 'No packages in cart'
      };
    }

    // For multiple packages, all must be valid
    const validationResults = orderedPackages.map(pkg =>
      validatePackageCompletion(pkg, filters.pax)
    );

    const allValid = validationResults.every(result => result.isValid);
    const allMissingCategories = validationResults.flatMap(result =>
      result.missingCategories || []
    );

    return {
      isValid: allValid,
      missingCategories: allMissingCategories,
      message: allValid
        ? 'All packages are complete'
        : `Missing items in ${allMissingCategories.length} categories across packages`
    };
  }, [orderedPackages, filters.pax]);

  // Check if there are items in the catering cart AND all required items are added
  const hasItemsInCart = cateringCartPackages.length > 0 || (orderedPackages && orderedPackages.length > 0);

  useEffect(() => {
    // Always use cateringCartPackages instead of selectedPackages prop
    if (cateringCartPackages && cateringCartPackages.length > 0) {
      setOrderedPackages(cateringCartPackages.map(pkg => ({
        ...pkg,
        items: Array.isArray(pkg.items) ? pkg.items.map(item => ({ ...item })) : []
      })));
    } else {
      setOrderedPackages([]);
    }
  }, [cateringCartPackages]);

  // Calculate total chargeable items cost across all packages
  const totalChargeableItemsCost = useMemo(() => {
    return calculateTotalChargeableItemsCost(orderedPackages, filters.pax);
  }, [orderedPackages, filters.pax]);

  // Calculate total package base prices
  const totalPackageBasePrice = useMemo(() => {
    return calculateTotalPackageBasePrice(orderedPackages, filters.pax);
  }, [orderedPackages, filters.pax]);

  // Calculate final total payable amount
  const totalPayable = useMemo(() => {
    return totalPackageBasePrice + totalChargeableItemsCost;
  }, [totalPackageBasePrice, totalChargeableItemsCost]);

  const handleBack = () => {
    // Clear the past order restoration flag when user goes back (handled by Redux)
    dispatch(setIsPastOrderRestoration(false));
    navigate(-1);
  };

  const handleShareQuotation = () => {
    console.log("Share Quotation clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleResendPaymentLink = async () => {
    try {
      setIsResendingPayment(true);
      setResendPaymentError(null);

      // Get the order ID from orderInfo (which should be set after successful order processing)
      if (!orderInfo || !orderInfo.order_id) {
        throw new Error('Order information not available. Cannot resend payment link.');
      }

      console.log('Resending payment link for order:', orderInfo.order_id);

      // Call the resend payment link API
      const response = await resendCateringPaymentLink(orderInfo.order_id);

      if (response && response.data) {
        console.log('Payment link resent successfully:', response.data);
        alert('Payment link has been resent successfully!');
      } else {
        throw new Error('Failed to resend payment link. Please try again.');
      }
    } catch (error) {
      console.error('Error resending payment link:', error);
      setResendPaymentError(error.message || 'Failed to resend payment link. Please try again.');
    } finally {
      setIsResendingPayment(false);
    }
  };

  const handleCateringOrder = async (payLater = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine customer and address based on service type
      let customerId, addressId, companyId;
      
      if (serviceType === "individual catering") {
        // For individual catering, use catering customer data
        if (!cateringCustomerInfo?.id) {
          throw new Error('Customer information is required for individual catering');
        }
        if (!cateringSelectedAddress?.id) {
          throw new Error('Address selection is required for individual catering');
        }
        customerId = cateringCustomerInfo.id;
        companyId = 0;
        addressId = cateringSelectedAddress.id;
      } else if (serviceType === "corporate catering") {
        // For corporate catering, use company data
        if (!companyInfo?.id) {
          throw new Error('Company information is required for corporate catering');
        }
        if (!companySelectedAddress?.id) {
          throw new Error('Company address selection is required for corporate catering');
        }
        customerId = 0;
        companyId = companyInfo.id;
        addressId = companySelectedAddress.id;
      } else {
        throw new Error('Invalid service type. Must be "individual catering" or "corporate catering"');
      }

      if (!orderedPackages || orderedPackages.length === 0) {
        throw new Error('No packages to process');
      }

      if (!cateringRestaurantId) {
        throw new Error('Restaurant information is required');
      }

      // Step 1: Empty existing catering cart
      const deleteCartPayload = {
        agent_id: "1",
        country_id: "3",
        restaurant_id: cateringRestaurantId.toString(),
        catering_id: orderedPackages[0].id.toString(), // Use first package ID
        customer_id: customerId.toString(),
        company_id: companyId.toString()
      };

      console.log('Deleting existing catering cart:', deleteCartPayload);
      await dispatch(emptyCateringCartAsync(deleteCartPayload));

      // Step 2: Add to catering cart
      const addToCartPayload = transformCateringCartPayload({
        customerId,
        companyId,
        addressId,
        packages: orderedPackages,
        filters,
        notes,
        restaurantId: cateringRestaurantId,
        branchId: 0, // Default for catering
        countryId: 3 // Default for Kuwait
      });

      console.log('Adding to catering cart:', addToCartPayload);
      const cartResponse = await addToCateringCart(addToCartPayload);
      
      if (!cartResponse || !cartResponse.data || !cartResponse.data.service_item_cart_id) {
        throw new Error('Failed to add items to cart');
      }

      console.log('Cart response:', cartResponse);

      // Step 3: Process catering manual order with new API
      const processOrderPayload = {
        country_id: "3",
        restaurant_id: cateringRestaurantId,
        catering_id: orderedPackages[0].id.toString(),
        customer_id: customerId.toString(),
        company_id: companyId.toString(),
        address_id: addressId,
        payment_method_id: "1",
        order_sub_total_price: totalPayable,
        order_total_price: totalPayable,
        discount_price: "0.000",
        currency: "KWD",
        is_discount_applied: "",
        promo_code: "",
        comments: notes || "",
        pay_later: payLater
      };

      console.log('Processing catering manual order:', processOrderPayload);
      const orderResponse = await processCateringManualOrder(processOrderPayload);
      if (!orderResponse || !orderResponse.data.data) {
        throw new Error('Failed to process order');
      }

      // Store order info for polling
      const orderData = orderResponse.data.data;
      setOrderInfo({
        order_id: orderData.order_id,
        order_no: orderData.order_no,
        reference_number: orderData.reference_number
      });

      // Success - show pending payment or completion based on pay_later
      setShowPendingPayment(true);
      setPaymentStatus(payLater ? "Booked" : "Pending");
      console.log('Catering order processed successfully:', orderResponse);
      
      // Save pending payment state to Redux (only for Pay Now)
      if (!payLater) {
        dispatch(setPendingPayment({
          status: "Pending",
          orderInfo: orderData,
          timestamp: new Date().toISOString()
        }));
      }
      
      // If pay_later is false (Pay Now), start polling for payment status
      if (!payLater && orderData.order_no) {
        startPaymentStatusPolling(orderData.order_no, orderData.reference_number);
      } else if (payLater) {
        // For Book Slot, payment is completed immediately, so handle success
        handlePaymentSuccess();
      }

    } catch (error) {
      console.error('Error processing catering order:', error);
      setError(error.message || 'Failed to process order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookSlot = () => handleCateringOrder(true);
  const handlePayNow = () => handleCateringOrder(false);

  // Start polling for payment status
  const startPaymentStatusPolling = (orderNo, referenceNumber = "") => {
    console.log('Starting payment status polling for order:', { orderNo, referenceNumber });
    
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll immediately first, then every 5 seconds
    const pollStatus = async () => {
      try {
        console.log('Polling payment status...');
        const statusResponse = await getCateringOrderStatus(orderNo, referenceNumber);
        console.log('Payment status poll response:', statusResponse);

        // The API returns the data directly, not nested in a data property
        if (statusResponse) {
          const { catering_order_status } = statusResponse;
          console.log('Current catering order status:', catering_order_status);
          
          // Update payment status based on catering_order_status
          if (catering_order_status === "Submitted" || catering_order_status === "Paid" || catering_order_status === "Accepted") {
            // Payment successful
            console.log('Payment successful, stopping polling');
            setPaymentStatus("Submitted");
            
            // Update Redux pending payment status
            dispatch(setPendingPayment({
              status: "Submitted",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
            
            stopPaymentStatusPolling();
            handlePaymentSuccess();
          } else if (catering_order_status === "Failed" || catering_order_status === "Canceled") {
            // Payment failed
            console.log('Payment failed, stopping polling');
            setPaymentStatus("Failed");
            
            // Update Redux pending payment status
            dispatch(setPendingPayment({
              status: "Failed",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
            
            stopPaymentStatusPolling();
          } else if (catering_order_status === "Pending Payment") {
            // Still pending, keep polling
            console.log('Payment still pending, continuing to poll...');
            setPaymentStatus("Pending");
            
            // Update Redux pending payment with latest timestamp
            dispatch(setPendingPayment({
              status: "Pending",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
        // Don't stop polling on error, might be temporary network issue
      }
    };

    // Poll immediately
    pollStatus();
    
    // Then poll every 5 seconds
    pollingIntervalRef.current = setInterval(pollStatus, 5000);
  };

  // Stop polling for payment status
  const stopPaymentStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle successful payment - reset cart and navigate
  const handlePaymentSuccess = () => {
    // Stop polling
    stopPaymentStatusPolling();
    
    // Clear pending payment state from Redux
    dispatch(clearPendingPayment());
    
    // Wait a moment to show success message, then reset and navigate
    setTimeout(() => {
      // Clear catering cart (this also clears isPastOrderRestoration and pastOrderAddressId in Redux)
      dispatch(clearCateringCart());
      
      // Reset filters
      dispatch(setCateringCartFilters({
        pax: 20,
        date: "",
        time: ""
      }));
      
      // Navigate to catering order page
      navigate("/catering-order");
    }, 2000); // 2 second delay to show success message
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPaymentStatusPolling();
    };
  }, []);

  // Payment status label logic similar to orderSummary
  const paymentStatusLabel = useMemo(() => {
    const statuses = [
      { status: "Pending", label: "Pending Payment" },
      { status: "Failed", label: "Payment Failed" },
      { status: "Submitted", label: "Payment Successful" },
      { status: "Booked", label: "Slot Booked" },
    ];
    return (
      statuses.find((s) => s.status === paymentStatus)?.label || paymentStatus
    );
  }, [paymentStatus]);

  return (
    <div className="w-full h-full bg-white flex flex-col items-center">
      <div className="w-full max-w-[500px] overflow-y-auto p-4 pb-40 flex flex-col">
        <div className="border border-gray-200 w-full p-4 rounded-md mt-5 flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-1">
            <h2 className="text-lg font-bold text-[#959FA8]">
              {cateringRestaurantName}
            </h2>
            <p className="text-sm text-[#959FA8]">{formattedDate}</p>
          </div>
          {orderedPackages && orderedPackages.length > 0 ? orderedPackages.map((pkg, index) => (
            <React.Fragment key={pkg.id}>
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-bold text-[#959FA8]">
                    {pkg?.name_en || pkg?.title}
                  </h2>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-lg font-bold text-[#959FA8]">
                    {filters.pax} PAX
                  </p>
                  <p className="text-lg font-bold text-[#959FA8]">
                    KWD {calculatePackagePrice(pkg, filters.pax).toFixed(3)}
                  </p>
                </div>

                {/* Display items for this specific package - READONLY */}
                {pkg.items && pkg.items.length > 0 && (
                  <div className="ml-4 mb-4">
                    {pkg.items.map((item) => (
                      <div key={item.id} className="mb-2">
                        {item.has_variants ? (
                          // Render variant items - READONLY
                          item.items?.map((variantItem, variantIndex) => (
                            <div
                              key={variantItem.variantId}
                              className="text-sm text-[#959FA8] font-semibold"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  <span className="mr-2 text-xs">
                                    {variantItem.quantity}x
                                  </span>
                                  <span>{item.name || item.name_en}</span>
                                </div>
                                <span>
                                  KWD{" "}
                                  {(() => {
                                    const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, variantItem, pkg, filters.pax);
                                    return totalDisplayPrice.toFixed(3);
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 ml-[30px]">
                                {getSelectedOptions(variantItem.selectedVariant)}
                              </div>
                              {variantItem.instructions && (
                                <div
                                  className="text-xs text-gray-400 ml-[30px] italic truncate"
                                  title={variantItem.instructions}
                                >
                                  Note: {variantItem.instructions}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          // Render non-variant items - READONLY
                          <div className="text-sm text-[#959FA8] font-semibold flex justify-between items-center">
                            <div className="flex items-center">
                              <span className="mr-2 text-xs">
                                {item.quantity}x
                              </span>
                              <span>{item.name_en || item.name}</span>
                            </div>
                            <span>
                              KWD{" "}
                              {(() => {
                                const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, null, pkg, filters.pax);
                                return totalDisplayPrice.toFixed(3);
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          )) : (
            <div className="text-center text-gray-500 py-8">
              No packages selected
            </div>
          )}

          {/* Setup Note Section */}
          {orderedPackages && orderedPackages.length > 0 && orderedPackages[0] && (
            <>
              <div className="mt-4">
                <img
                  src="/Images/Menu/hr.png"
                  className="w-full mb-3"
                  alt="separator"
                />
                <h2 className="text-lg font-bold text-[#959FA8] mb-3">
                  Setup Note
                </h2>
                <div className="space-y-2">
                  {/* Service Duration */}
                  {orderedPackages[0].service_duration && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Service Duration:</span> {orderedPackages[0].service_duration}
                      </div>
                    </div>
                  )}
                  
                  {/* Setup Time */}
                  {orderedPackages[0].setup_time && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Setup Time:</span> {orderedPackages[0].setup_time}
                      </div>
                    </div>
                  )}
                  
                  {/* Electric Outlet */}
                  {orderedPackages[0].electric_outlet && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Electric Outlet:</span> Required
                      </div>
                    </div>
                  )}
                  
                  {/* Water Supply */}
                  {orderedPackages[0].water_supply && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Water Supply:</span> {orderedPackages[0].water_supply_specs || 'Required'}
                      </div>
                    </div>
                  )}
                  
                  {/* Special Notes */}
                  {orderedPackages[0].special_notes_en && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Special Notes:</span> {orderedPackages[0].special_notes_en}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="mt-4">
            <img
              src="/Images/Menu/hr.png"
              className="w-full mb-4"
              alt="separator"
            />
            
            {/* Total Payment Section */}
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <h2 className="text-lg font-bold text-[#959FA8]">Total Payment</h2>
              </div>
              
              <div className="space-y-2">
                {/* Service Items (Base Package Price) */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Service Items</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalPackageBasePrice.toFixed(3)}</span>
                </div>
                
                {/* Add Ons */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Add Ons</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalChargeableItemsCost.toFixed(3)}</span>
                </div>
                
                {/* Additional Add Ons */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Additional Add Ons</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                {/* Delivery Charges */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Delivery Charges</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                {/* VAT */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">VAT</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                <div className="border-t border-gray-300 my-2"></div>
                
                {/* Subtotal */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Subtotal</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                </div>
                
                {/* Discount */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Discount</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                <div className="border-t border-gray-400 my-2"></div>
                
                {/* Total Amount */}
                <div className="flex justify-between">
                  <span className="text-sm font-bold text-[#959FA8]">Total Amount</span>
                  <span className="text-sm font-bold text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 w-full">
            <textarea
              className="border border-gray-300 w-full h-[100px] p-3 rounded-md text-sm font-semibold text-[#959FA8]"
              placeholder="Notes;"
              value={notes}
              onChange={(e) => dispatch(setCateringCartNotes(e.target.value))}
            ></textarea>
          </div>


          {/* Payment status display - similar to orderSummary */}
          {showPendingPayment && (
            <div className="flex flex-col justify-center items-center mt-8 mb-4">
              <button
                className={`w-full sm:w-[222px] h-10 text-lg font-semibold p-2 rounded ${
                  paymentStatusLabel === "Pending Payment"
                    ? "bg-[#F7B501] text-white" // Styles for Pending Payment
                    : paymentStatusLabel === "Payment Failed"
                    ? "bg-[#F70101] text-white" // Styles for Payment Failed
                    : paymentStatusLabel === "Payment Successful"
                    ? "bg-[#00AF0D] text-white" // Styles for Payment Successful
                    : paymentStatusLabel === "Slot Booked"
                    ? "bg-[#00AF0D] text-white" // Styles for Slot Booked
                    : ""
                }`}
              >
                {paymentStatusLabel}
              </button>

              {/* Additional text below status */}
              {paymentStatusLabel === "Pending Payment" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Payment link has been sent to the customer. Waiting payment
                    to be completed.
                  </p>
                </div>
              )}
              {paymentStatusLabel === "Slot Booked" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Catering slot has been successfully booked. Payment can be completed later.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error message display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Processing Order
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {error}
                  </p>
                  <button 
                    onClick={() => setError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Resend Payment Link Error Display */}
          {resendPaymentError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Resending Payment Link
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {resendPaymentError}
                  </p>
                  <button 
                    onClick={() => setResendPaymentError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="w-full bg-white pt-4">
            <div className="flex flex-col items-center">
              {showPaymentOptions ? (
                // Catering Checkout Page Buttons
                <div className="flex flex-col gap-3 w-full max-w-[412px]">
                  {/* Share Quotation Button */}
                  {/* <button
                    onClick={handleShareQuotation}
                    className="w-full h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center"
                  >
                    Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                  </button> */}
                  
                  <div className="flex flex-row gap-3">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                    >
                      Back
                    </button>
                    {showPendingPayment ? (
                      <button
                        onClick={handleResendPaymentLink}
                        disabled={isResendingPayment}
                        className={`flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors ${
                          isResendingPayment ? "opacity-50" : ""
                        }`}
                      >
                        <div className="flex items-center justify-around">
                          <span className="text-sm px-2">
                            {isResendingPayment ? "Resending..." : "Resend Payment Link"}
                          </span>
                          {!isResendingPayment && (
                            <img
                              src="/Images/Menu/resend.svg"
                              alt="resend"
                              className="ml-2"
                            />
                          )}
                        </div>
                      </button>
                    ) : (
                      <div className="flex flex-col gap-2 flex-1">
                        <button
                          onClick={handleBookSlot}
                          disabled={isLoading}
                          className={`h-10 text-sm font-semibold bg-[#13305D] text-white p-2 rounded hover:bg-[#0F2749] transition-colors ${
                            isLoading ? "opacity-50" : ""
                          }`}
                        >
                          {isLoading ? "Processing..." : "Book Slot (Pay Later)"}
                        </button>
                        <button
                          onClick={handlePayNow}
                          disabled={isLoading}
                          className={`h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors ${
                            isLoading ? "opacity-50" : ""
                          }`}
                        >
                          {isLoading ? "Processing..." : "Pay Now"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* {showShareModal && <ShareQuotationModal onClose={handleCloseShareModal} />} */}
    </div>
  );
};

export default CateringOrderSummaryReadonly;
